import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react'

import { Box, Tabs as MuiTabs, Tab, useTheme } from '@mui/material'

import { TabData } from './types'

export const CustomTabs: FC<TabData<string>> = ({ tabs, activeTab, setActiveTab }) => {
  const { spacing, palette, breakpoints } = useTheme()

  const tabRefs = useRef<(HTMLDivElement | null)[]>([])

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue)
    },
    [setActiveTab],
  )

  useEffect(() => {
    const activeTabRef = tabRefs.current[activeTab]

    if (!activeTabRef) return

    const timeoutId = setTimeout(() => {
      activeTabRef.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }, 100) // delay a bit to avoid rapid calls

    return () => clearTimeout(timeoutId)
  }, [activeTab])

  const renderTabs = useMemo(
    () =>
      tabs.map((tab, i) => (
        <Tab
          key={tab.key}
          id={`tab-${i}`}
          aria-controls={`tabpanel-${i}`}
          ref={(el) => (tabRefs.current[i] = el)}
          value={i}
          label={
            tab.badgeLabel ? (
              <Box
                display="inline-flex"
                alignItems="center"
                gap={0.75}
                sx={{ whiteSpace: 'nowrap' }}
              >
                <Box component="span">{tab.label}</Box>
                <Box
                  component="span"
                  sx={{
                    px: 0.75,
                    py: 0.125,
                    borderRadius: 999,
                    bgcolor: palette.primary.main,
                    color: palette.common.white,
                    fontSize: 10,
                    fontWeight: 800,
                    lineHeight: 1.5,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}
                >
                  {tab.badgeLabel}
                </Box>
              </Box>
            ) : (
              tab.label
            )
          }
          disabled={tab.disabled}
          className={tab.className}
          disableRipple
          sx={{
            color: palette.grey[700],
            fontSize: 14,
            fontWeight: 800,
            lineHeight: spacing(3),
            letterSpacing: spacing(0.05),
            textTransform: 'initial',
            '&.Mui-selected': {
              color: palette.primary.main,
            },
          }}
        />
      )),
    [tabs, palette, spacing],
  )

  return (
    // We need box for normal tabs height
    <Box
      sx={{
        overflowX: 'auto',
        [breakpoints.down('sm')]: {
          width: '100%',
        },
      }}
    >
      <MuiTabs
        selectionFollowsFocus
        value={activeTab}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons={false}
        TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
        sx={{
          flexGrow: 1,
          minHeight: spacing(5),
          '& .MuiTabs-indicator': {
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'transparent',
          },
          '& .MuiTabs-indicatorSpan': {
            maxWidth: 40,
            width: '100%',
            backgroundColor: palette.primary.main,
          },
        }}
      >
        {renderTabs}
      </MuiTabs>
    </Box>
  )
}
